/**
 * Identity linking for the D-044 layer.
 *
 * Every call is explicit: importing this module never opens a wallet or asks
 * for a signature. Callers link at the moment of value (a claim, a drop, an
 * `invoked -> connected` step), never at boot.
 */
import { getToken } from './authService';
import * as core from '../../services/coreClient';

function authToken(token) {
  const value = token || getToken();
  if (!value) throw new Error('A core session is required to link an identity');
  return value;
}

export function hasLinkedWallet(identities = []) {
  return (Array.isArray(identities) ? identities : []).some(
    (row) => row?.provider === 'wallet' && row?.status === 'verified',
  );
}

export function linkedProviders(identities = []) {
  return (Array.isArray(identities) ? identities : [])
    .filter((row) => row?.status === 'verified')
    .map((row) => row.provider);
}

async function signMessage(signer, message, address) {
  if (typeof signer === 'function') return signer(message, address);
  if (signer && typeof signer.signMessage === 'function') {
    return signer.signMessage(message);
  }
  if (typeof signer === 'string') return signer;
  throw new Error('A wallet signer is required');
}

export async function linkWallet({ provider = 'base', address, signer, token } = {}) {
  if (!address) throw new Error('A wallet address is required');
  const nonce = await core.getWalletNonce(address);
  if (!nonce?.message) throw new Error('The server did not issue a wallet message');
  const signature = await signMessage(signer, nonce.message, address);
  return core.linkIdentity(authToken(token), {
    provider,
    address: String(address).toLowerCase(),
    signature,
  });
}

export async function linkFarcaster({ jwt, token } = {}) {
  if (!jwt) throw new Error('A Farcaster Quick Auth token is required');
  return core.linkIdentity(authToken(token), { provider: 'farcaster', jwt });
}

export async function linkWorldWallet({ address, signer, token } = {}) {
  return linkWallet({ provider: 'world', address, signer, token });
}

export async function linkWorldId({ idkit, token } = {}) {
  if (!idkit) throw new Error('A World ID result is required');
  return core.linkIdentity(authToken(token), { provider: 'world_id', idkit });
}

export async function loadIdentity(token) {
  return core.listIdentities(authToken(token));
}

export default {
  hasLinkedWallet,
  linkedProviders,
  linkFarcaster,
  linkWallet,
  linkWorldId,
  linkWorldWallet,
  loadIdentity,
};
